import type { ParameterTimeSeriesConfiguration, ParametersUnit } from "@/lib/api";

export type ParameterWithUnits = {
  units: ParametersUnit[];
};

export function intersectUnitCodes(parameters: ParameterWithUnits[]): ParametersUnit[] {
  if (parameters.length === 0) return [];

  const [first, ...rest] = parameters;
  const sharedCodes = new Set(first.units.map((unit) => unit.code));

  for (const parameter of rest) {
    const codes = new Set(parameter.units.map((unit) => unit.code));
    for (const code of [...sharedCodes]) {
      if (!codes.has(code)) sharedCodes.delete(code);
    }
  }

  return first.units.filter((unit) => sharedCodes.has(unit.code));
}

export function parameterSupportsUnitCode(
  parameter: ParameterWithUnits,
  unitCode: string | undefined,
): boolean {
  if (!unitCode) return true;
  return parameter.units.some((unit) => unit.code === unitCode);
}

export function resolveUnitFromCatalog(
  units: ParametersUnit[],
  unitCode: string,
): ParametersUnit | undefined {
  return units.find((unit) => unit.code === unitCode);
}

export function resolveFetchUnitForParameter(
  parameterId: string,
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
): string | undefined {
  for (const config of timeSeriesConfigurations) {
    const inGroup = config.series.some((series) => series.parameter_id === parameterId);
    if (inGroup) return config.unit?.code;
  }
  return undefined;
}

export function configuredTimeSeriesParameterIdSet(
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
): Set<string> {
  return new Set(
    timeSeriesConfigurations.flatMap((config) =>
      config.series.map((series) => series.parameter_id),
    ),
  );
}

export function pickAutoUnitCode(
  overlappingUnits: ParametersUnit[],
  preferredUnitCode: string | undefined,
): string | undefined {
  if (overlappingUnits.length === 0) return undefined;
  if (preferredUnitCode) {
    const match = overlappingUnits.find((unit) => unit.code === preferredUnitCode);
    if (match) return match.code;
  }
  return overlappingUnits[0].code;
}
